// src/components/calendar/test/CalendarComponentsTest.jsx
import React, { useState } from 'react';
import {
  CalendarHourLabel,
  CalendarTimeSlot,
  CalendarDateHeader,
  CalendarGrid,
  CalendarOverflowIndicator,
  EventStatusBadge,
  PageContainer
} from '../../ui';

const CalendarComponentsTest = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [clickLog, setClickLog] = useState([]);

  // Helper to log clicks
  const logClick = (message) => {
    const timestamp = new Date().toLocaleTimeString();
    setClickLog(prev => [`${timestamp}: ${message}`, ...prev.slice(0, 9)]);
  };

  // Test data
  const testHours = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17];
  const testDates = Array.from({ length: 7 }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - date.getDay() + i);
    return date;
  });
  const today = new Date();
  const statuses = ['pending', 'confirmed', 'in_progress', 'completed', 'cancelled', 'requires_followup'];

  return (
    <PageContainer>
      <div className="space-y-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-slate-200 mb-2">
            Calendar Components Test Page
          </h1>
          <p className="text-slate-400">
            Testing all 6 pure display components before calendar refactoring
          </p>
        </div>

        {/* Click Log */}
        <div className="bg-slate-800/30 rounded-xl p-4 border border-slate-700/50">
          <h3 className="text-lg font-semibold text-slate-200 mb-3">Click Log (Last 10)</h3>
          <div className="space-y-1 h-40 overflow-y-auto">
            {clickLog.length === 0 ? (
              <p className="text-slate-400 text-sm">Click components below to test interactions...</p>
            ) : (
              clickLog.map((log, index) => (
                <div key={index} className="text-sm text-slate-300 font-mono bg-slate-700/30 px-2 py-1 rounded">
                  {log}
                </div>
              ))
            )}
          </div>
        </div>

        {/* 1. CalendarHourLabel Tests */}
        <div className="bg-slate-800/30 rounded-xl p-6 border border-slate-700/50">
          <h2 className="text-xl font-semibold text-slate-200 mb-4">1. CalendarHourLabel</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Different sizes */}
            <div>
              <h4 className="text-sm font-medium text-slate-300 mb-3">Sizes</h4>
              <div className="space-y-2">
                <div className="flex items-center">
                  <CalendarHourLabel hour={9} size="sm" />
                  <span className="ml-3 text-sm text-slate-400">Small</span>
                </div>
                <div className="flex items-center">
                  <CalendarHourLabel hour={10} size="default" />
                  <span className="ml-3 text-sm text-slate-400">Default</span>
                </div>
                <div className="flex items-center">
                  <CalendarHourLabel hour={11} size="lg" />
                  <span className="ml-3 text-sm text-slate-400">Large</span>
                </div>
              </div>
            </div>

            {/* Different formats */}
            <div>
              <h4 className="text-sm font-medium text-slate-300 mb-3">Formats</h4>
              <div className="space-y-2">
                <div className="flex items-center">
                  <CalendarHourLabel hour={14} format="12" />
                  <span className="ml-3 text-sm text-slate-400">12-hour</span>
                </div>
                <div className="flex items-center">
                  <CalendarHourLabel hour={14} format="24" />
                  <span className="ml-3 text-sm text-slate-400">24-hour</span>
                </div>
              </div>
            </div>

            {/* Different positions */}
            <div>
              <h4 className="text-sm font-medium text-slate-300 mb-3">Positions</h4>
              <div className="space-y-2">
                <CalendarHourLabel hour={9} position="left" />
                <CalendarHourLabel hour={10} position="center" />
                <CalendarHourLabel hour={11} position="right" />
              </div>
            </div>
          </div>
        </div>

        {/* 2. CalendarDateHeader Tests */}
        <div className="bg-slate-800/30 rounded-xl p-6 border border-slate-700/50">
          <h2 className="text-xl font-semibold text-slate-200 mb-4">2. CalendarDateHeader</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Week view */}
            <div>
              <h4 className="text-sm font-medium text-slate-300 mb-3">Week View</h4>
              <div className="space-y-3">
                <CalendarDateHeader date={today} view="week" isToday={true} />
                <CalendarDateHeader date={testDates[1]} view="week" isSelected={true} />
                <CalendarDateHeader date={testDates[2]} view="week" />
              </div>
            </div>

            {/* Month view */}
            <div>
              <h4 className="text-sm font-medium text-slate-300 mb-3">Month View</h4>
              <div className="space-y-3">
                <CalendarDateHeader date={today} view="month" isToday={true} />
                <CalendarDateHeader date={testDates[1]} view="month" isCurrentMonth={false} />
                <CalendarDateHeader date={testDates[2]} view="month" isSelected={true} />
              </div>
            </div>

            {/* Day view */}
            <div>
              <h4 className="text-sm font-medium text-slate-300 mb-3">Day View</h4>
              <div className="space-y-3">
                <CalendarDateHeader date={today} view="day" isToday={true} />
                <CalendarDateHeader date={testDates[1]} view="day" />
              </div>
            </div>
          </div>
        </div>

        {/* 3. CalendarTimeSlot Tests */}
        <div className="bg-slate-800/30 rounded-xl p-6 border border-slate-700/50">
          <h2 className="text-xl font-semibold text-slate-200 mb-4">3. CalendarTimeSlot</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <CalendarTimeSlot
              date={today}
              hour={9}
              isClickable={true}
              onClick={(date, hour) => logClick(`Time slot clicked: ${date.toLocaleDateString()} at ${hour}:00`)}
            >
              <div className="p-2 text-sm text-slate-300">Regular slot (clickable)</div>
            </CalendarTimeSlot>

            <CalendarTimeSlot
              date={today}
              hour={10}
              isAvailable={true}
              isClickable={true}
              onClick={(date, hour) => logClick(`Available slot clicked: ${date.toLocaleDateString()} at ${hour}:00`)}
            >
              <div className="p-2 text-sm text-slate-300">Available slot</div>
            </CalendarTimeSlot>

            <CalendarTimeSlot
              date={today}
              hour={11}
              height="h-20"
            >
              <div className="p-2 text-sm text-slate-300">Taller slot</div>
            </CalendarTimeSlot>

            <CalendarTimeSlot
              date={today}
              hour={12}
              isClickable={true}
              onClick={(date, hour) => logClick(`Slot with content clicked: ${date.toLocaleDateString()} at ${hour}:00`)}
            >
              <div className="p-2">
                <EventStatusBadge status="confirmed" size="xs" />
                <div className="text-xs text-slate-300 mt-1">Meeting</div>
              </div>
            </CalendarTimeSlot>
          </div>
        </div>

        {/* 4. EventStatusBadge Tests */}
        <div className="bg-slate-800/30 rounded-xl p-6 border border-slate-700/50">
          <h2 className="text-xl font-semibold text-slate-200 mb-4">4. EventStatusBadge</h2>
          
          <div className="space-y-4">
            {/* All statuses - default variant */}
            <div>
              <h4 className="text-sm font-medium text-slate-300 mb-3">All Statuses (Default)</h4>
              <div className="flex flex-wrap gap-2">
                {statuses.map(status => (
                  <EventStatusBadge key={status} status={status} />
                ))}
              </div>
            </div>

            {/* Different sizes */}
            <div>
              <h4 className="text-sm font-medium text-slate-300 mb-3">Sizes</h4>
              <div className="flex items-center gap-4">
                <EventStatusBadge status="confirmed" size="xs" />
                <EventStatusBadge status="confirmed" size="sm" />
                <EventStatusBadge status="confirmed" size="md" />
              </div>
            </div>

            {/* Different variants */}
            <div>
              <h4 className="text-sm font-medium text-slate-300 mb-3">Variants</h4>
              <div className="flex items-center gap-4">
                <EventStatusBadge status="in_progress" variant="default" />
                <EventStatusBadge status="in_progress" variant="minimal" />
                <EventStatusBadge status="in_progress" variant="icon-only" />
              </div>
            </div>
          </div>
        </div>

        {/* 5. CalendarOverflowIndicator Tests */}
        <div className="bg-slate-800/30 rounded-xl p-6 border border-slate-700/50">
          <h2 className="text-xl font-semibold text-slate-200 mb-4">5. CalendarOverflowIndicator</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h4 className="text-sm font-medium text-slate-300 mb-3">Regular Overflow</h4>
              <div className="space-y-2">
                <CalendarOverflowIndicator 
                  count={2} 
                  onClick={() => logClick('Overflow clicked: +2 more')}
                />
                <CalendarOverflowIndicator 
                  count={5} 
                  size="md"
                  onClick={() => logClick('Overflow clicked: +5 more')}
                />
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-slate-300 mb-3">With Preview</h4>
              <div className="space-y-2">
                <CalendarOverflowIndicator 
                  count={1} 
                  hasPreview={true}
                  onClick={() => logClick('Preview overflow clicked')}
                />
                <CalendarOverflowIndicator 
                  count={0} 
                  hasPreview={true}
                  onClick={() => logClick('Preview only clicked')}
                />
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-slate-300 mb-3">Different Variants</h4>
              <div className="space-y-2">
                <CalendarOverflowIndicator 
                  count={3} 
                  variant="default"
                  onClick={() => logClick('Default variant')}
                />
                <CalendarOverflowIndicator 
                  count={3} 
                  variant="compact"
                  onClick={() => logClick('Compact variant')}
                />
              </div>
            </div>
          </div>
        </div>

        {/* 6. CalendarGrid Tests */}
        <div className="bg-slate-800/30 rounded-xl p-6 border border-slate-700/50">
          <h2 className="text-xl font-semibold text-slate-200 mb-4">6. CalendarGrid</h2>
          
          <div className="space-y-6">
            {/* Week grid */}
            <div>
              <h4 className="text-sm font-medium text-slate-300 mb-3">Week Grid (7 columns)</h4>
              <CalendarGrid type="week" className="bg-slate-700/20 rounded-lg overflow-hidden">
                {testDates.map((date, index) => (
                  <div key={index} className="p-4 text-center min-h-16 flex items-center justify-center">
                    <CalendarDateHeader date={date} view="week" isToday={date.toDateString() === today.toDateString()} />
                  </div>
                ))}
              </CalendarGrid>
            </div>

            {/* Month grid preview */}
            <div>
              <h4 className="text-sm font-medium text-slate-300 mb-3">Month Grid (7 columns)</h4>
              <CalendarGrid type="month" className="bg-slate-700/20 rounded-lg overflow-hidden">
                {Array.from({ length: 14 }, (_, i) => {
                  const date = new Date(today);
                  date.setDate(today.getDate() - today.getDay() + i);
                  return (
                    <div key={i} className="p-2 min-h-20 flex flex-col">
                      <CalendarDateHeader 
                        date={date} 
                        view="month" 
                        isToday={date.toDateString() === today.toDateString()}
                        isCurrentMonth={date.getMonth() === today.getMonth()}
                      />
                      {i % 3 === 0 && (
                        <div className="mt-1">
                          <EventStatusBadge status="confirmed" size="xs" variant="icon-only" />
                        </div>
                      )}
                      {i % 5 === 0 && i > 0 && (
                        <CalendarOverflowIndicator count={2} size="xs" />
                      )}
                    </div>
                  );
                })}
              </CalendarGrid>
            </div>
          </div>
        </div>

        {/* 7. Integration Test - Mini Week View */}
        <div className="bg-slate-800/30 rounded-xl p-6 border border-slate-700/50">
          <h2 className="text-xl font-semibold text-slate-200 mb-4">7. Integration Test - Mini Week View</h2>
          
          <div className="bg-slate-700/20 rounded-lg overflow-hidden">
            {/* Header */}
            <CalendarGrid type="week" className="bg-slate-800/40 border-b border-slate-600/30">
              <div className="w-14 p-2"></div> {/* Spacer for hour column */}
              {testDates.map((date, index) => (
                <CalendarDateHeader
                  key={index}
                  date={date}
                  view="week"
                  isToday={date.toDateString() === today.toDateString()}
                />
              ))}
            </CalendarGrid>

            {/* Time slots */}
            {[9, 10, 11].map(hour => (
              <div key={hour} className="flex border-b border-slate-600/20">
                <CalendarHourLabel hour={hour} />
                <CalendarGrid type="week">
                  {testDates.map((date, dayIndex) => (
                    <CalendarTimeSlot
                      key={dayIndex}
                      date={date}
                      hour={hour}
                      isClickable={true}
                      isAvailable={dayIndex === 2 && hour === 10} // Make Wednesday 10 AM available
                      onClick={(date, hour) => logClick(`Integration test: ${date.toLocaleDateString()} at ${hour}:00`)}
                    >
                      {/* Add some test events */}
                      {dayIndex === 1 && hour === 9 && (
                        <div className="p-1">
                          <EventStatusBadge status="confirmed" size="xs" />
                          <div className="text-xs text-slate-300">Meeting</div>
                        </div>
                      )}
                      {dayIndex === 4 && hour === 11 && (
                        <div className="p-1">
                          <EventStatusBadge status="in_progress" size="xs" />
                          <div className="text-xs text-slate-300">Call</div>
                        </div>
                      )}
                    </CalendarTimeSlot>
                  ))}
                </CalendarGrid>
              </div>
            ))}
          </div>
        </div>

        {/* Success Checklist */}
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-6">
          <h2 className="text-xl font-semibold text-emerald-300 mb-4">✅ Success Checklist</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-emerald-400 rounded-full"></div>
                <span className="text-slate-300">All components import without errors</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-emerald-400 rounded-full"></div>
                <span className="text-slate-300">Styling matches design system</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-emerald-400 rounded-full"></div>
                <span className="text-slate-300">Click events work (check log above)</span>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-emerald-400 rounded-full"></div>
                <span className="text-slate-300">Hover effects functional</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-emerald-400 rounded-full"></div>
                <span className="text-slate-300">Components work together</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-emerald-400 rounded-full"></div>
                <span className="text-slate-300">No console errors</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export default CalendarComponentsTest;